import { useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { dateInputToUnix } from '@/shared/lib/dateInput';
import { formatNumber } from '@/shared/lib/formatNumber';
import { DateTimeInput } from '@/shared/ui/DateTimeInput';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { fetchDashboard, type DashboardData } from './api';
import {
  BID_SOURCE_OPTIONS,
  CITY_OPTIONS,
  CURRENCY_SYMBOL,
  DASHBOARD_METRICS,
  SEGMENT_OPTIONS,
  TOTAL_INCOME_FIELD,
  type MetricType,
} from './constants';

const DATE_FORMAT = 'YYYY-MM-DD';
const FULL = { width: '100%' as const };

const SUFFIX_BY_TYPE: Record<MetricType, string> = {
  currency: CURRENCY_SYMBOL,
  count: '',
  percent: '%',
};

const formatValue = (data: DashboardData | undefined, field: string, type: MetricType) =>
  `${formatNumber(data?.[field] || '0')}${SUFFIX_BY_TYPE[type]}`;

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="stat-online-field">
    <span className="stat-online-label">{label}</span>
    {children}
  </div>
);

export const StatOnlinePage = () => {
  const today = dayjs().format(DATE_FORMAT);
  const [cityId, setCityId] = useState('');
  const [segment, setSegment] = useState('all');
  const [bidSource, setBidSource] = useState('all');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  const params = {
    cityId,
    segment,
    bidSource,
    timeFrom: dateInputToUnix(dateFrom),
    timeTo: dateInputToUnix(dateTo, 'end'),
  };
  const { data, isLoading, error } = useQuery({
    queryKey: ['stat-dashboard', params],
    queryFn: () => fetchDashboard(params),
    placeholderData: keepPreviousData,
  });

  return (
    <section className="content">
      <div className="stat-online-filters">
        <div className="stat-online-col">
          <Field label="Город">
            <FilterSelect
              {...FULL}
              options={CITY_OPTIONS}
              value={cityId}
              placeholder="Все города"
              onChange={setCityId}
            />
          </Field>
        </div>
        <div className="stat-online-col">
          <Field label="Сегмент">
            <FilterSelect
              {...FULL}
              options={SEGMENT_OPTIONS}
              value={segment}
              isClearable={false}
              onChange={setSegment}
            />
          </Field>
          <Field label="Источник заявок">
            <FilterSelect
              {...FULL}
              options={BID_SOURCE_OPTIONS}
              value={bidSource}
              isClearable={false}
              onChange={setBidSource}
            />
          </Field>
        </div>
        <div className="stat-online-col">
          <Field label="Дата от">
            <DateTimeInput
              {...FULL}
              type="date"
              value={dateFrom}
              max={dateTo || today}
              onChange={setDateFrom}
            />
          </Field>
          <Field label="Дата до">
            <DateTimeInput {...FULL} type="date" value={dateTo} min={dateFrom} max={today} onChange={setDateTo} />
          </Field>
        </div>
      </div>
      <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки статистики" />
      <div className="box">
        <div className="box-body no-padding">
          <table className="table table-striped">
            <tbody>
              {DASHBOARD_METRICS.map(({ name, field, type }) => (
                <tr key={field}>
                  <td>{name}</td>
                  <td className="text-right">{formatValue(data, field, type)}</td>
                </tr>
              ))}
              <tr className="bg-light-blue">
                <td>
                  <b>Итого заработано UREMONT</b>
                </td>
                <td className="text-right">
                  <b>{formatValue(data, TOTAL_INCOME_FIELD, 'currency')}</b>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};
